import React, { useEffect, useRef } from "react";

import "./news.less";

import priceBg from "@assets/images/official_website_new/price_bg.jpg";
import noDataIcon from "@assets/images/no_data_new_light.svg";
import errorIcon from "@assets/images/load_fail.svg";
import NewsCard from "@components/news_card";
import { formatNewsPublishDate } from "../../shared/news";
import useNewsList from "../../shared/useNewsList";
import { scrollPageToTop } from "../../shared/scrollTop";

/** 首屏骨架卡数量（4 列时铺满两行） */
const NEWS_SKELETON_COUNT = 8;

/** 续页骨架兜底数量：接口未返回 total 时使用 */
const NEWS_MORE_SKELETON_FALLBACK = 4;

/** 哨兵距视口底部 200px 时就拉下一页 */
const LOAD_MORE_ROOT_MARGIN = "0px 0px 200px 0px";

/**
 * 资讯页 / Web + Pad —— 结构参考 pc/price/PricePage.jsx：
 * 自己 import 自己的 news.less、返回自己的根容器，由 pc/App.jsx 在 `view === "news"` 时渲染。
 *
 * 当前包含顶部 Banner、分类 Tab 与资讯卡片列表；列表滚动到底自动续页，详情后续补充。
 * 取数 / 分页 / 错误态 / 空态与移动端共用 shared/useNewsList，两端只有触发续页的方式不同。
 * Pad 横 / 竖屏由 .ow-scale-root 上的 .ow-pad-land / .ow-pad-port 控制尺寸。
 */
export default function NewsPage({ onOpenDetail }) {
  /* 底部触发哨兵 */
  const sentinelRef = useRef(null);

  const {
    tabs,
    activeCategory,
    articles,
    listLoading,
    loadingMore,
    hasMore,
    hasError,
    showPinned,
    moreSkeletonCount,
    loadMore,
    changeCategory,
    retry,
  } = useNewsList({ moreSkeletonFallback: NEWS_MORE_SKELETON_FALLBACK });

  /**
   * 点击分类 Tab：切换分类后把页面滚动条拉回顶部。
   *
   * 不复用 useNewsList 的 onReset —— 那个回调只在分类真的变化时才触发，
   * 而「点当前已选中的 Tab」同样应该回到顶部（用户常把它当「回顶部」用），
   * 所以置顶放在点击处、无条件执行。
   * 用 scrollPageToTop 而不是 window.scrollTo：页面滚到列表中部时切换，
   * 正文会先换成骨架再变高，iOS Safari 的平滑滚动在中途被打断会停在半路。
   */
  const handleTabClick = (value) => {
    changeCategory(value);
    scrollPageToTop();
  };

  /* 触底续页：哨兵进入「视口 + 200px」时拉下一页；加载中 / 到底 / 出错都不挂观察器 */
  useEffect(() => {
    if (!hasMore || listLoading || loadingMore || hasError) return undefined;

    const node = sentinelRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        loadMore();
      },
      { rootMargin: LOAD_MORE_ROOT_MARGIN }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, listLoading, loadingMore, hasError, loadMore]);

  return (
    <div className="news-page">
      {/* —— 顶部 Banner：结构参考 price-hero，背景图复用 price_bg.jpg —— */}
      <section
        className="news-hero"
        style={{ backgroundImage: `url("${priceBg}")` }}
      >
        <h1 className="news-hero__title">品牌数字化建设，从此刻开始</h1>
        <p className="news-hero__sub">更多个性建站技巧、私域营销干货，持续更新中</p>
      </section>

      {/* —— 产品列表：顶部为分类 Tab，卡片接 /api/news/articles，滚动到底自动续页 —— */}
      <section className="news-list-section">
        {!hasError && tabs.length > 0 ? (
          <div className="news-tabs" role="group" aria-label="资讯分类">
            {tabs.map((tab) => {
              const isActive = tab.value === activeCategory;

              return (
                <button
                  key={tab.value}
                  type="button"
                  title={tab.label}
                  aria-pressed={isActive}
                  className={`news-tab${isActive ? " is-active" : ""}`}
                  onClick={() => handleTabClick(tab.value)}
                >
                  <span className="news-tab__label">{tab.label}</span>
                </button>
              );
            })}
          </div>
        ) : null}

        {hasError ? (
          <div className="news-error" role="alert">
            <div className="news-error__content">
              <img className="news-error__icon" src={errorIcon} alt="" />
              <p className="news-error__title">内容加载失败</p>
              <p className="news-error__desc">请检查网络后重试</p>
            </div>
            <button
              type="button"
              className="news-error__retry"
              onClick={retry}
            >
              重新加载
            </button>
          </div>
        ) : listLoading ? (
          <div className="news-list" aria-busy="true">
            {Array.from({ length: NEWS_SKELETON_COUNT }, (_, index) => (
              <NewsCard key={`news-skeleton-${index}`} skeleton />
            ))}
          </div>
        ) : articles.length > 0 ? (
          <>
            <div className="news-list" aria-busy={loadingMore}>
              {articles.map((article) => (
                <NewsCard
                  key={article.id}
                  brandName="BUJUE"
                  typeName={article.categoryLabel || ""}
                  title={article.title || ""}
                  coverUrl={article.cover || ""}
                  pinned={showPinned && Boolean(article.pinned)}
                  category={article.categoryLabel || ""}
                  summary={article.summary || ""}
                  teamName={article.author || ""}
                  publishDate={formatNewsPublishDate(article.tmPublished)}
                  onClick={() => onOpenDetail?.(article.id, article.title)}
                />
              ))}
              {loadingMore
                ? Array.from({ length: moreSkeletonCount }, (_, index) => (
                    <NewsCard key={`news-more-skeleton-${index}`} skeleton />
                  ))
                : null}
            </div>
            {hasMore && !loadingMore ? (
              <div
                ref={sentinelRef}
                className="news-list__sentinel"
                aria-hidden="true"
              />
            ) : null}
          </>
        ) : (
          <div className="news-empty" role="status">
            <img className="news-empty__icon" src={noDataIcon} alt="" />
            <p className="news-empty__text">暂无资讯内容</p>
          </div>
        )}
      </section>
    </div>
  );
}
